/* =========================================================
   Cart drawer upsells
   - <cart-upsells> loads automatic recommendations and drives the slider
   - Delegated listeners handle variant changes and add to cart
   Lightweight, no dependencies. Works with the drawer being re-rendered.
   ========================================================= */
(function () {
  'use strict';

  if (customElements.get('cart-upsells')) return;

  var cache = new Map();

  function getHTML(url, key) {
    var cacheKey = url + '|' + (key || '');
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    var request = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (text) {
        if (!text) return '';
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var results = doc.querySelector('[data-cu-results]');
        return results && results.querySelector('[data-cu-card]') ? results.innerHTML.trim() : '';
      })
      .catch(function () {
        cache.delete(cacheKey);
        return '';
      });

    cache.set(cacheKey, request);
    return request;
  }

  class CartUpsells extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cu-track]');
      this.prev = this.querySelector('[data-cu-prev]');
      this.next = this.querySelector('[data-cu-next]');
      this.onScroll = this.onScroll.bind(this);

      if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prev) this.prev.addEventListener('click', this.slide.bind(this, -1));
      if (this.next) this.next.addEventListener('click', this.slide.bind(this, 1));

      if (this.dataset.url) {
        this.load();
      } else {
        this.updateNav();
      }
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    }

    load() {
      var self = this;
      var url = this.dataset.url;
      var key = this.dataset.signature || '';
      var fallbackUrl = this.dataset.intent === 'complementary' ? url.replace('intent=complementary', 'intent=related') : '';

      getHTML(url, key)
        .then(function (html) {
          if (html || !fallbackUrl) return html;
          return getHTML(fallbackUrl, key);
        })
        .then(function (html) {
          if (!self.isConnected || !self.track) return;
          if (html) {
            self.track.innerHTML = html;
            self.classList.add('is-loaded');
            self.track.scrollLeft = 0;
          }
          self.hidden = !self.track.querySelector('[data-cu-card]');
          self.updateNav();
        });
    }

    slide(direction) {
      if (!this.track) return;
      var card = this.track.querySelector('[data-cu-card]');
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      var step = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
      var rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({ left: step * direction * rtl, behavior: 'smooth' });
    }

    onScroll() {
      if (this.frame) return;
      var self = this;
      this.frame = requestAnimationFrame(function () {
        self.frame = null;
        self.updateNav();
      });
    }

    updateNav() {
      if (!this.track || (!this.prev && !this.next)) return;
      var max = this.track.scrollWidth - this.track.clientWidth;
      var position = Math.abs(this.track.scrollLeft);
      var scrollable = max > 2;

      if (this.prev) {
        this.prev.hidden = !scrollable;
        this.prev.disabled = position <= 2;
      }
      if (this.next) {
        this.next.hidden = !scrollable;
        this.next.disabled = position >= max - 2;
      }
    }
  }

  if (!window.customElements.get('cart-upsells')) {
    window.customElements.define('cart-upsells', CartUpsells);
  }

  /* Variant picker ------------------------------------------------------ */
  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-cu-variant]')) return;

    var card = select.closest('[data-cu-card]');
    var option = select.options[select.selectedIndex];
    if (!card || !option) return;

    var price = card.querySelector('[data-cu-price]');
    var compare = card.querySelector('[data-cu-compare]');
    var button = card.querySelector('[data-cu-add]');

    if (price) price.textContent = option.dataset.price || '';
    if (compare) compare.textContent = option.dataset.compare || '';
    if (button) {
      button.dataset.variantId = option.value;
      button.disabled = option.disabled;
      button.classList.remove('is-added');
    }
  });

  /* Add to cart --------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var button = event.target instanceof Element ? event.target.closest('[data-cu-add]') : null;
    if (!button || button.disabled || button.classList.contains('is-loading')) return;

    event.preventDefault();

    var root = button.closest('cart-upsells');
    var status = root ? root.querySelector('[data-cu-status]') : null;
    var select = button.closest('[data-cu-card]') ? button.closest('[data-cu-card]').querySelector('[data-cu-variant]') : null;
    var variantId = parseInt(select ? select.value : button.dataset.variantId, 10);
    if (!variantId) return;

    var shain = window.ShainPro || {};
    var routes = shain.routes || {};
    var strings = shain.strings || {};

    if (status) status.textContent = '';
    button.classList.add('is-loading');

    fetch((routes.cartAdd || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw data;
          return data;
        });
      })
      .then(function () {
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        document.dispatchEvent(new CustomEvent('cart-upsells:added', { detail: { variantId: variantId } }));
        return new Promise(function (resolve) {
          setTimeout(resolve, 450);
        });
      })
      .then(function () {
        if (window.ShainProCart && typeof window.ShainProCart.refresh === 'function') {
          return window.ShainProCart.refresh();
        }
        return null;
      })
      .catch(function (error) {
        button.classList.remove('is-loading', 'is-added');
        if (status) {
          status.textContent = (error && error.description) || strings.cartError || 'Something went wrong. Please try again.';
        }
      });
  });

  /* Theme editor: keep the drawer open while styling ------------------- */
  function openInEditor() {
    var drawer = document.querySelector('[data-cu-editor-open]');
    var shain = window.ShainPro || {};
    if (drawer && shain.Drawers && typeof shain.Drawers.open === 'function') {
      shain.Drawers.open(drawer);
    }
  }

  if (window.Shopify && window.Shopify.designMode) {
    if (document.readyState === 'complete') {
      openInEditor();
    } else {
      window.addEventListener('load', openInEditor);
    }
  }
})();